import {
  all,
  call,
  put,
  takeEvery,
  cancelled,
  select,
} from "redux-saga/effects";
import { SagaIterator } from "redux-saga";
import { JsonApiResponse } from "@stemscopes/api";
import axios from "axios";
import { endpoints } from "configs";
import { request, generatePath } from "utils";
import * as actions from "./actions";
import * as Types from "./types";

function* fetchComments({
  payload: filter,
}: ReturnType<typeof actions.fetchComments.request>): SagaIterator {
  const source = axios.CancelToken.source();
  try {
    const {
      data: { data },
    }: JsonApiResponse<Types.FetchedComments> = yield call(
      request.get,
      filter.studentAssignmentId
        ? endpoints.studentAssignmentComments
        : endpoints.comments,
      {
        params: filter.studentAssignmentId
          ? { studentAssignmentId: filter.studentAssignmentId }
          : {
              filter,
            },
        cancelToken: source.token,
      },
    );

    yield put(actions.fetchComments.success(undefined, undefined));
    yield put(
      actions.setComments(
        (data.comments
          ? [...data.comments, ...(data.studentAssignmentComments || [])]
          : data
        ).map((comment) => ({ ...comment, mode: "show" })),
      ),
    );
  } catch (error) {
    yield put(actions.fetchComments.failure(undefined, undefined));
  } finally {
    if (yield cancelled()) {
      source.cancel();
    }
  }
}

function* createComment({
  payload: comment,
}: ReturnType<typeof actions.createComment.request>): SagaIterator {
  try {
    const { filter, wholeSectionComments } = yield select(
      (state) => state.toolbar,
    );
    const response: JsonApiResponse<Types.Comment> = yield call(
      request.post,
      filter.studentAssignmentId
        ? endpoints.studentAssignmentComments
        : endpoints.comments,
      {
        [filter.studentAssignmentId ? "studentAssignmentComment" : "comment"]: {
          ...comment,
          ...filter,
        },
      },
    );

    yield put(
      actions.setComments(
        wholeSectionComments.map((item: Types.Comment) =>
          !item.id ? response.data.data : item,
        ),
      ),
    );

    yield put(actions.createComment.success(undefined, undefined));
  } catch (error) {
    yield put(actions.createComment.failure(undefined, undefined));
  }
}

function* updateComment({
  payload: comment,
}: ReturnType<typeof actions.updateComment.request>): SagaIterator {
  try {
    const { filter, wholeSectionComments } = yield select(
      (state) => state.toolbar,
    );
    const response: JsonApiResponse<Types.Comment> = yield call(
      request.put,
      generatePath(
        filter.studentAssignmentId
          ? endpoints.studentAssignmentComment
          : endpoints.comment,
        { id: comment.id },
      ),
      {
        [filter.studentAssignmentId ? "studentAssignmentComment" : "comment"]:
          comment,
      },
    );

    yield put(
      actions.setComments(
        wholeSectionComments.map((item: Types.Comment) =>
          item.id === response.data.data.id ? response.data.data : item,
        ),
      ),
    );

    yield put(actions.updateComment.success(undefined, undefined));
  } catch (error) {
    yield put(actions.updateComment.failure(undefined, undefined));
  }
}

function* removeComment({
  payload: id,
}: ReturnType<typeof actions.removeComment.request>): SagaIterator {
  try {
    const { filter, wholeSectionComments } = yield select(
      (state) => state.toolbar,
    );

    yield put(
      actions.setComments(
        wholeSectionComments.filter(
          (comment: Types.Comment) => comment.id !== id,
        ),
      ),
    );

    if (id) {
      yield call(
        request.delete,
        generatePath(
          filter.studentAssignmentId
            ? endpoints.studentAssignmentComment
            : endpoints.comment,
          { id },
        ),
      );
    }

    yield put(actions.removeComment.success(undefined, undefined));
  } catch (error) {
    yield put(actions.removeComment.failure(undefined, undefined));
  }
}

export default function* rootSaga(): SagaIterator {
  yield all([takeEvery(actions.fetchComments.request, fetchComments)]);
  yield all([takeEvery(actions.createComment.request, createComment)]);
  yield all([takeEvery(actions.updateComment.request, updateComment)]);
  yield all([takeEvery(actions.removeComment.request, removeComment)]);
}
